import React, { useState } from "react";
import ViewAndEditUploadedFile from "./ViewAndEditUploadedFile";
import "../../css/UploadDocument.css";
import "../../css/ImportDocument.css";

function UploadDocument({ onBack, onNext, enhancedRightClickEnabled, showContextMenu, ContextMenuComponent }) {
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [documentTitle, setDocumentTitle] = useState("");
    const [selectedFile, setSelectedFile] = useState(null);
    const [imageAttachments, setImageAttachments] = useState([]);
    const [showViewAndEdit, setShowViewAndEdit] = useState(false);


    const handleBackClick = () => {
        if (onBack) {
            setIsTransitioning(true);
            setTimeout(() => {
                onBack();
            }, 150);
        }
    };

    const handleNextClick = () => {
        // Validate document title before proceeding
        if (!documentTitle || documentTitle.trim() === "") {
            alert("Please enter a document title.");
            return;
        }

        // Check that title only contains letters, numbers, spaces, periods, dashes, and underscores
        if (!/^[a-zA-Z0-9\s\.\-\_]+$/.test(documentTitle)) {
            alert(
                "Document title must contain only letters, numbers, spaces, periods, dashes, and underscores."
            );
            return;
        }

        if (!selectedFile) {
            alert("Please select an HTML or Markdown file to upload.");
            return;
        }

        setIsTransitioning(true);
        setTimeout(() => {
            setShowViewAndEdit(true);
        }, 150);
    };

    const handleBackFromViewAndEdit = () => {
        setShowViewAndEdit(false);
        setIsTransitioning(false);
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedFile(file);
            
            // Auto-populate document title with filename (without extension)
            if (!documentTitle) {
                const fileName = file.name.replace(/\.[^/.]+$/, ""); // Remove file extension
                // Clean the filename to match validation requirements
                const cleanedName = fileName.replace(/[^a-zA-Z0-9\s\.\-\_]/g, "");
                setDocumentTitle(cleanedName);
            }
        }
    };

    const handleImageAttachmentsChange = (event) => {
        const files = Array.from(event.target.files);
        setImageAttachments(files);
    };

    const removeImageAttachment = (indexToRemove) => {
        setImageAttachments(prev => prev.filter((_, index) => index !== indexToRemove));
    };

    const handleDragOver = (event) => {
        event.preventDefault();
    };

    const isSupportedImportFile = (file) => {
        return file.type === "text/html" ||
            file.type === "text/markdown" ||
            /\.(html|md|markdown|mdown|mkd)$/i.test(file.name);
    };

    const handleDrop = (event) => {
        event.preventDefault();
        const files = event.dataTransfer.files;
        if (files.length > 0) {
            const file = files[0];
            if (isSupportedImportFile(file)) {
                setSelectedFile(file);

                // Auto-populate document title with filename (without extension)
                if (!documentTitle) {
                    const fileName = file.name.replace(/\.[^/.]+$/, "");
                    const cleanedName = fileName.replace(/[^a-zA-Z0-9\s\.\-\_]/g, "");
                    setDocumentTitle(cleanedName);
                }
            } else {
                alert("Please select an HTML or Markdown file.");
            }
        }
    };

    // Maximum length for document title (same as Create.js Page limit)
    const maxTitleLength = 150;

    if (showViewAndEdit) {
        return (
            <ViewAndEditUploadedFile 
                onBack={handleBackFromViewAndEdit}
                documentTitle={documentTitle}
                selectedFile={selectedFile}
                imageAttachments={imageAttachments}
                enhancedRightClickEnabled={enhancedRightClickEnabled}
                showContextMenu={showContextMenu}
                ContextMenuComponent={ContextMenuComponent}
            />
        );
    }

    return (
        <div 
            className={`ImportDocument__main-div ${isTransitioning ? 'transitioning' : ''}`}
            onContextMenu={
                enhancedRightClickEnabled
                ? (e) => {
                    e.preventDefault();
                    showContextMenu(e.clientX, e.clientY);
                    }
                : undefined
            }
        >
            <div className="ImportDocument__content">
                <h2>Time to upload your file!</h2>

                <p className="medium" style={{ marginTop: "20px" }}>
                    Please select the HTML or Markdown (.md) file you exported from your previous documentation platform.
                </p>

                {/* Document Title Field */}
                <div style={{ marginTop: "30px", marginBottom: "20px" }}>
                    <label style={{ 
                        display: "block", 
                        marginBottom: "8px", 
                        fontWeight: "bold",
                        color: "var(--bb-text)"
                    }}>
                        Document Title:
                    </label>
                    <input
                        type="text"
                        value={documentTitle}
                        onChange={(e) => setDocumentTitle(e.target.value)}
                        maxLength={maxTitleLength}
                        placeholder={`Enter document title (max ${maxTitleLength} characters)`}
                        style={{
                            width: "100%",
                            padding: "12px",
                            borderRadius: "6px",
                            border: "2px solid var(--bb-text)",
                            backgroundColor: "rgba(255, 255, 255, 0.9)",
                            color: "#000",
                            fontSize: "16px",
                            boxSizing: "border-box"
                        }}
                        required
                    />
                    <p style={{ 
                        fontSize: "12px", 
                        color: "var(--bb-text)", 
                        marginTop: "5px",
                        opacity: "0.8"
                    }}>
                        {documentTitle.length}/{maxTitleLength} characters. 
                        Only letters, numbers, spaces, periods, dashes, and underscores allowed.
                    </p>
                </div>

                {/* File upload area */}
                <div 
                    style={{ 
                        border: '2px dashed var(--bb-text)', 
                        borderRadius: '8px', 
                        padding: '40px', 
                        textAlign: 'center', 
                        marginTop: '20px',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        transition: 'all 0.3s ease'
                    }}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                >
                    <p style={{ marginBottom: "15px" }}>
                        {selectedFile ? `Selected: ${selectedFile.name}` : "Drag and drop your HTML or Markdown file here, or click to browse"}
                    </p>
                    <input
                        type="file"
                        accept=".html,text/html,.md,.markdown,.mdown,.mkd,text/markdown"
                        onChange={handleFileChange}
                        style={{ 
                            marginTop: '10px',
                            padding: '8px',
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            border: '1px solid var(--bb-text)',
                            borderRadius: '4px',
                            width: '100%',
                            maxWidth: '100%',
                            boxSizing: 'border-box'
                        }} 
                    />
                    {selectedFile && (
                        <p style={{ 
                            marginTop: "10px", 
                            color: "green", 
                            fontWeight: "bold",
                            fontSize: "14px"
                        }}>
                            ✓ File ready for upload
                        </p>
                    )}
                </div>

                {selectedFile && (
                    <div style={{ 
                        marginTop: "15px", 
                        padding: "10px", 
                        backgroundColor: "rgba(76, 175, 80, 0.1)", 
                        border: "1px solid #4CAF50", 
                        borderRadius: "4px",
                        color: "#2E7D32",
                        textAlign: "center"
                    }}>
                        <p style={{ margin: "0", fontWeight: "bold" }}>
                            ✓ File selected! Click the right arrow to continue.
                        </p>
                    </div>
                )}

                {/* Image Attachments Section */}
                <div style={{ marginTop: "30px", marginBottom: "20px" }}>
                    <label style={{ 
                        display: "block", 
                        marginBottom: "8px", 
                        fontWeight: "bold",
                        color: "var(--bb-text)"
                    }}>
                        Image Attachments (Optional):
                    </label>
                    <p style={{ 
                        fontSize: "14px", 
                        color: "var(--bb-text)", 
                        marginBottom: "10px",
                        opacity: "0.8"
                    }}>
                        Upload any images that are referenced in your HTML file. We'll automatically match them by filename.
                    </p>
                    <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleImageAttachmentsChange}
                        style={{
                            width: '100%',
                            padding: '12px',
                            borderRadius: '6px',
                            border: '2px solid var(--bb-text)',
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            fontSize: '16px',
                            boxSizing: 'border-box',
                            maxWidth: '100%'
                        }}
                    />
                    {imageAttachments.length > 0 && (
                        <div style={{ marginTop: "15px" }}>
                            <p style={{ 
                                fontSize: "14px", 
                                fontWeight: "bold",
                                color: "var(--bb-text)",
                                marginBottom: "10px"
                            }}>
                                Selected Images ({imageAttachments.length}):
                            </p>
                            <div style={{ 
                                maxHeight: "150px", 
                                overflowY: "auto",
                                border: "1px solid var(--bb-text)",
                                borderRadius: "4px",
                                padding: "10px",
                                backgroundColor: "rgba(255, 255, 255, 0.9)"
                            }}>
                                {imageAttachments.map((file, index) => (
                                    <div key={index} style={{ 
                                        display: "flex", 
                                        justifyContent: "space-between", 
                                        alignItems: "center",
                                        padding: "5px 0",
                                        borderBottom: index < imageAttachments.length - 1 ? "1px solid #eee" : "none"
                                    }}>
                                        <span style={{ 
                                            fontSize: "13px",
                                            color: "#333",
                                            flex: 1,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap"
                                        }}>
                                            📷 {file.name} ({(file.size / 1024).toFixed(1)} KB)
                                        </span>
                                        <button
                                            onClick={() => removeImageAttachment(index)}
                                            style={{
                                                marginLeft: "10px",
                                                padding: "2px 6px",
                                                backgroundColor: "#ff5630",
                                                color: "white",
                                                border: "none",
                                                borderRadius: "3px",
                                                fontSize: "12px",
                                                cursor: "pointer"
                                            }}
                                            title="Remove image"
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div style={{ marginTop: "25px" }}>
                    <p className="medium">
                        Importing a <strong>Markdown (.md)</strong> file? Just select it above — we'll convert
                        headings, lists, tables, task lists, code blocks, and other Markdown formatting automatically.
                        You'll be able to review everything before finalizing.
                    </p>
                    <p className="medium">
                        As we improve the import feature, we'll add instructions for exporting documents from other platforms.
                        For now, <strong>here are instructions below for exporting/importing documents from Confluence</strong>.
                    </p>
                    <ol>
                        <li>
                            Navigate to your space in confluence. You should see your space name, "All Content", and 
                            "Space Settings" on the top left.
                        </li>
                        <li>
                            Click on "Space Settings".
                        </li>
                        <li>
                            Under "General", click on "Export space".
                        </li>
                        <li>
                            Be sure to choose the HTML format option, choose whether you want to export all content or select content, and 
                            then click "Export Space".
                        </li>
                        <li>
                            You should now have a ZIP file downloaded to your computer. Extract this ZIP file to access the HTML files.
                        </li>
                        <li>
                            Open the extracted folder and locate the HTML files. Upload the specific HTML file you want above.
                        </li>
                        <li>
                            If your HTML file references images, you can also upload those images in the section above. <i>You can find the 
                            images referenced in attachments directory of the extracted folder</i>.
                        </li>
                    </ol>
                </div>
            </div>

            {/* Back Arrow */}
            <div 
                className="ImportDocument__arrow ImportDocument__arrow--back" 
                onClick={handleBackClick}
                title="Go back"
                aria-label="Go back to previous step"
            >
                ❮
            </div>

            {/* Forward Arrow */}
            <div 
                className="ImportDocument__arrow ImportDocument__arrow--forward" 
                onClick={handleNextClick}
                title="Continue to next step"
                aria-label="Continue to next step"
            >
                ❯
            </div>
            {enhancedRightClickEnabled && ContextMenuComponent}
        </div>
    );
}

export default UploadDocument;